import type { IconType } from "react-icons";
import {
  HiOutlineAcademicCap,
  HiOutlineArrowRightOnRectangle,
  HiOutlineBell,
  HiOutlineBookOpen,
  HiOutlineCalendar,
  HiOutlineCalendarDays,
  HiOutlineChartBar,
  HiOutlineClipboardDocumentList,
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlineCreditCard,
  HiOutlineCog6Tooth,
  HiOutlineMusicalNote,
  HiOutlinePuzzlePiece,
  HiOutlineDocumentText,
  HiOutlineEnvelope,
  HiOutlineHome,
  HiOutlineUserGroup,
  HiOutlineUserPlus,
  HiOutlineUsers,
  HiOutlineSun,
  HiOutlineBuildingOffice,
  HiOutlineArrowPathRoundedSquare,
  HiOutlineLockClosed,
  HiOutlineQuestionMarkCircle,
  HiOutlineBanknotes,
  HiOutlineBriefcase,
  HiOutlineCalculator,
  HiOutlineRectangleStack,
  HiOutlineTicket,
} from "react-icons/hi2";
import { PageTypeAdmin, PageTypeGlobal, PageTypePortal } from "@/lib/types";
import { DEFAULT_CONTENT_SETTINGS } from "@/lib/utils/content-settings/content-settings-defaults";

export interface LinkItem {
  href: string;
  label: string;
  icon: IconType;
  active?: boolean;
}

export interface LinkGroup {
  id?: string;
  label?: string;
  icon?: IconType;
  items: LinkItem[];
  collapsible?: boolean;
}

type NavGroupDefinition = {
  id?: string;
  label?: string;
  icon?: IconType;
  collapsible?: boolean;
  items: Omit<LinkItem, "active">[];
};

export type StudioFeatureNavFlags = {
  makeupsEnabled?: boolean;
  appointmentsEnabled?: boolean;
  timeClockEnabled?: boolean;
  choreographerPortalEnabled?: boolean;
  contractorsEnabled?: boolean;
  ticketsEnabled?: boolean;
};

export type StudioPortalNavLabels = {
  students?: string;
  appointments?: string;
  makeups?: string;
  terms?: string;
};

export type StudioNavOptions = {
  features?: StudioFeatureNavFlags;
  portalLabels?: StudioPortalNavLabels;
};

const DEFAULT_FEATURES: Required<StudioFeatureNavFlags> = {
  makeupsEnabled: true,
  appointmentsEnabled: true,
  timeClockEnabled: true,
  choreographerPortalEnabled: false,
  contractorsEnabled: false,
  ticketsEnabled: false,
};

function resolveFeatureFlags(
  features?: StudioFeatureNavFlags,
): Required<StudioFeatureNavFlags> {
  return {
    makeupsEnabled: features?.makeupsEnabled ?? DEFAULT_FEATURES.makeupsEnabled,
    appointmentsEnabled:
      features?.appointmentsEnabled ?? DEFAULT_FEATURES.appointmentsEnabled,
    timeClockEnabled:
      features?.timeClockEnabled ?? DEFAULT_FEATURES.timeClockEnabled,
    choreographerPortalEnabled:
      features?.choreographerPortalEnabled ??
      DEFAULT_FEATURES.choreographerPortalEnabled,
    contractorsEnabled:
      features?.contractorsEnabled ?? DEFAULT_FEATURES.contractorsEnabled,
    ticketsEnabled:
      features?.ticketsEnabled ?? DEFAULT_FEATURES.ticketsEnabled,
  };
}

function isModuleHrefEnabled(
  href: string,
  features: Required<StudioFeatureNavFlags>,
): boolean {
  if (href === PageTypePortal.Makeups || href === PageTypeAdmin.MakeupRequests) {
    return features.makeupsEnabled;
  }
  if (
    href === PageTypePortal.Appointments ||
    href === PageTypeAdmin.Appointments ||
    href === PageTypeAdmin.Availability
  ) {
    return features.appointmentsEnabled;
  }
  if (href === PageTypeAdmin.TimeClock) {
    return features.timeClockEnabled;
  }
  if (href === PageTypeAdmin.Dances) {
    return features.choreographerPortalEnabled;
  }
  if (href === PageTypeAdmin.Contractors) {
    return features.contractorsEnabled;
  }
  if (href === PageTypeAdmin.Tickets) {
    return features.ticketsEnabled;
  }
  return true;
}

function applyPortalLabels(
  item: Omit<LinkItem, "active">,
  labels?: StudioPortalNavLabels,
): Omit<LinkItem, "active"> {
  if (!labels) return item;

  switch (item.href) {
    case PageTypePortal.Students:
      return {
        ...item,
        label: labels.students?.trim() || DEFAULT_CONTENT_SETTINGS.portalNavStudentsLabel,
      };
    case PageTypePortal.Appointments:
      return {
        ...item,
        label:
          labels.appointments?.trim() ||
          DEFAULT_CONTENT_SETTINGS.portalNavAppointmentsLabel,
      };
    case PageTypePortal.Makeups:
      return {
        ...item,
        label:
          labels.makeups?.trim() || DEFAULT_CONTENT_SETTINGS.portalNavMakeupsLabel,
      };
    case PageTypePortal.Terms:
      return {
        ...item,
        label: labels.terms?.trim() || DEFAULT_CONTENT_SETTINGS.portalNavTermsLabel,
      };
    default:
      return item;
  }
}

function filterNavItems(
  items: Omit<LinkItem, "active">[],
  options?: StudioNavOptions,
): Omit<LinkItem, "active">[] {
  const features = resolveFeatureFlags(options?.features);

  return items
    .filter((item) => isModuleHrefEnabled(item.href, features))
    .map((item) => applyPortalLabels(item, options?.portalLabels));
}

// Portal/Member links - ordered by priority and usage frequency
// Organized: Primary → Core Actions → Financial → Support & Information
const linkItemsMemberBase: Omit<LinkItem, "active">[] = [
  // Primary navigation
  {
    href: PageTypePortal.Dashboard,
    label: "Dashboard",
    icon: HiOutlineHome,
  },
  {
    href: PageTypePortal.Balance,
    label: "Balance",
    icon: HiOutlineCreditCard,
  },
  {
    href: PageTypePortal.Students,
    label: "Students",
    icon: HiOutlineUsers,
  },
  // Core actions (most frequently used)
  {
    href: PageTypePortal.ClassRegistration,
    label: "Classes",
    icon: HiOutlineClipboardDocumentList,
  },
  {
    href: PageTypePortal.Appointments,
    label: "Private Lessons",
    icon: HiOutlineCalendar,
  },
  {
    href: PageTypePortal.Makeups,
    label: "Makeup requests",
    icon: HiOutlineArrowPathRoundedSquare,
  },
  {
    href: PageTypePortal.Notifications,
    label: "Notifications",
    icon: HiOutlineBell,
  },
  // Support & information
  {
    href: PageTypePortal.Contact,
    label: "Contact",
    icon: HiOutlineEnvelope,
  },
  {
    href: PageTypePortal.Terms,
    label: "Waivers",
    icon: HiOutlineDocumentText,
  },
  {
    href: PageTypePortal.Resources,
    label: "Resources",
    icon: HiOutlineQuestionMarkCircle,
  },
];

/** Logged-in members browsing public marketing pages (not full portal sidebar). */
const linkItemsPublicMemberBase: Omit<LinkItem, "active">[] = [
  {
    href: PageTypePortal.Dashboard,
    label: "Dashboard",
    icon: HiOutlineHome,
  },
  {
    href: PageTypeGlobal.Classes,
    label: "Classes",
    icon: HiOutlineBookOpen,
  },
  {
    href: PageTypeGlobal.Teachers,
    label: "Instructors",
    icon: HiOutlineUserGroup,
  },
  {
    href: PageTypeGlobal.Locations,
    label: "Locations",
    icon: HiOutlineBuildingOffice,
  },
  {
    href: PageTypeGlobal.Tickets,
    label: "Tickets",
    icon: HiOutlineTicket,
  },
  {
    href: PageTypeGlobal.Terms,
    label: "Terms",
    icon: HiOutlineDocumentText,
  },
  {
    href: PageTypeGlobal.Contact,
    label: "Contact",
    icon: HiOutlineEnvelope,
  },
];

// Guest/Public links - ordered by priority for new visitors
const linkItemsGuestBase: Omit<LinkItem, "active">[] = [
  // Authentication (primary actions)
  {
    href: PageTypePortal.Login,
    label: "Login",
    icon: HiOutlineArrowRightOnRectangle,
  },
  {
    href: PageTypePortal.SignUp,
    label: "Sign Up",
    icon: HiOutlineUserPlus,
  },
  // Public information
  {
    href: PageTypeGlobal.Classes,
    label: "Classes",
    icon: HiOutlineBookOpen,
  },
  {
    href: PageTypeGlobal.Teachers,
    label: "Instructors",
    icon: HiOutlineUserGroup,
  },
  {
    href: PageTypeGlobal.Locations,
    label: "Locations",
    icon: HiOutlineBuildingOffice,
  },
  {
    href: PageTypeGlobal.Tickets,
    label: "Tickets",
    icon: HiOutlineTicket,
  },
  {
    href: PageTypeGlobal.Terms,
    label: "Terms",
    icon: HiOutlineDocumentText,
  },
  {
    href: PageTypeGlobal.Contact,
    label: "Contact",
    icon: HiOutlineEnvelope,
  },
];

// Admin links - organized by groups: Daily Operation, Periodic Operation, Settings
// Dashboard is separate and appears first, before groups
const adminDashboardLink: Omit<LinkItem, "active"> = {
  href: PageTypeAdmin.Dashboard,
  label: "Dashboard",
  icon: HiOutlineHome,
};

const ADMIN_LINKS_WITH_SUB_ROUTES: PageTypeAdmin[] = [
  PageTypeAdmin.Classes,
  PageTypeAdmin.Parents,
  PageTypeAdmin.Students,
  PageTypeAdmin.Teachers,
  PageTypeAdmin.Employees,
  PageTypeAdmin.Dances,
  PageTypeAdmin.Appointments,
  PageTypeAdmin.Facilities,
  PageTypeAdmin.Seasons,
  PageTypeAdmin.Policies,
  PageTypeAdmin.Jobs,
  PageTypeAdmin.Reports,
  PageTypeAdmin.TimeClock,
  PageTypeAdmin.Tickets,
];

function mapAdminNavLinkActive(
  item: Omit<LinkItem, "active">,
  pathname: string,
): LinkItem {
  const pathSegments = pathname.split("/").filter(Boolean);
  const hrefSegments = item.href.split("/").filter(Boolean);

  const matches = hrefSegments.every((seg, i) => pathSegments[i] === seg);
  const isExact = pathSegments.length === hrefSegments.length;

  const hasSubRoutes = ADMIN_LINKS_WITH_SUB_ROUTES.includes(
    item.href as PageTypeAdmin,
  );
  const isFacilitiesLink = item.href === PageTypeAdmin.Facilities;
  const isActive = isFacilitiesLink
    ? pathname.startsWith(PageTypeAdmin.Facilities) ||
      pathname.startsWith(PageTypeAdmin.Locations) ||
      pathname.startsWith(PageTypeAdmin.ClassRooms)
    : hasSubRoutes
      ? pathname.startsWith(item.href)
      : matches && isExact;

  return {
    ...item,
    active: isActive,
  };
}

function mapAdminNavGroups(
  sections: NavGroupDefinition[],
  pathname: string,
  options?: StudioNavOptions,
): LinkGroup[] {
  return sections.map((section) => ({
    id: section.id,
    label: section.label,
    icon: section.icon,
    collapsible: section.collapsible,
    items: filterNavItems(section.items, options).map((item) =>
      mapAdminNavLinkActive(item, pathname),
    ),
  }));
}

const adminNavSectionsBase: NavGroupDefinition[] = [
  {
    items: [
      {
        href: PageTypeAdmin.Classes,
        label: "Classes",
        icon: HiOutlineBookOpen,
      },
      {
        href: PageTypeAdmin.Students,
        label: "Students",
        icon: HiOutlineUsers,
      },
      {
        href: PageTypeAdmin.Parents,
        label: "Parents",
        icon: HiOutlineUserGroup,
      },
      {
        href: PageTypeAdmin.Appointments,
        label: "Private Lessons",
        icon: HiOutlineCalendarDays,
      },
    ],
  },
  {
    id: "billing",
    label: "Billing",
    icon: HiOutlineCreditCard,
    collapsible: true,
    items: [
      {
        href: PageTypeAdmin.Tuition,
        label: "Post Tuition",
        icon: HiOutlineCalculator,
      },
      {
        href: PageTypeAdmin.Invoices,
        label: "Billing & Invoices",
        icon: HiOutlineBanknotes,
      },
      {
        href: PageTypeAdmin.Balance,
        label: "Balance & Payments",
        icon: HiOutlineCreditCard,
      },
    ],
  },
  {
    id: "operations",
    label: "Operations",
    icon: HiOutlineClipboardDocumentList,
    collapsible: true,
    items: [
      {
        href: PageTypeAdmin.MakeupRequests,
        label: "Makeup requests",
        icon: HiOutlineArrowPathRoundedSquare,
      },
      {
        href: PageTypeAdmin.TimeClock,
        label: "Time Clock",
        icon: HiOutlineClock,
      },
      {
        href: PageTypeAdmin.Tasks,
        label: "Tasks",
        icon: HiOutlineCheckCircle,
      },
      {
        href: PageTypeAdmin.Tickets,
        label: "Tickets",
        icon: HiOutlineTicket,
      },
      {
        href: PageTypeAdmin.Reports,
        label: "Reports",
        icon: HiOutlineChartBar,
      },
      {
        href: PageTypeAdmin.Notifications,
        label: "Notifications",
        icon: HiOutlineBell,
      },
    ],
  },
  {
    id: "foundation",
    label: "Foundation",
    icon: HiOutlineRectangleStack,
    collapsible: true,
    items: [
      {
        href: PageTypeAdmin.Employees,
        label: "Employees",
        icon: HiOutlineAcademicCap,
      },
      {
        href: PageTypeAdmin.Contractors,
        label: "Contractors",
        icon: HiOutlineBriefcase,
      },
      {
        href: PageTypeAdmin.Dances,
        label: "Dances",
        icon: HiOutlineMusicalNote,
      },
      {
        href: PageTypeAdmin.Seasons,
        label: "Seasons",
        icon: HiOutlineSun,
      },
      {
        href: PageTypeAdmin.Facilities,
        label: "Facilities",
        icon: HiOutlineBuildingOffice,
      },
      {
        href: PageTypeAdmin.Policies,
        label: "Waivers & Policies",
        icon: HiOutlineDocumentText,
      },
    ],
  },
  {
    items: [
      {
        href: PageTypeAdmin.Plugins,
        label: "Plugins",
        icon: HiOutlinePuzzlePiece,
      },
      {
        href: PageTypeAdmin.Settings,
        label: "Settings",
        icon: HiOutlineCog6Tooth,
      },
    ],
  },
  {
    items: [
      {
        href: PageTypeAdmin.Resources,
        label: "Resources",
        icon: HiOutlineQuestionMarkCircle,
      },
    ],
  },
];

const staffDashboardLink: Omit<LinkItem, "active"> = {
  href: PageTypeAdmin.Dashboard,
  label: "Dashboard",
  icon: HiOutlineHome,
};

const STAFF_LINKS_WITH_SUB_ROUTES: PageTypeAdmin[] = [
  PageTypeAdmin.Classes,
  PageTypeAdmin.Parents,
  PageTypeAdmin.Students,
  PageTypeAdmin.Employees,
  PageTypeAdmin.Dances,
  PageTypeAdmin.Appointments,
  PageTypeAdmin.Policies,
  PageTypeAdmin.TimeClock,
  PageTypeAdmin.Tickets,
];

function mapStaffNavLinkActive(
  item: Omit<LinkItem, "active">,
  pathname: string,
): LinkItem {
  const pathSegments = pathname.split("/").filter(Boolean);
  const hrefSegments = item.href.split("/").filter(Boolean);
  const matches = hrefSegments.every((seg, i) => pathSegments[i] === seg);
  const isExact = pathSegments.length === hrefSegments.length;
  const hasSubRoutes = STAFF_LINKS_WITH_SUB_ROUTES.includes(
    item.href as PageTypeAdmin,
  );
  const isActive = hasSubRoutes
    ? pathname.startsWith(item.href)
    : matches && isExact;

  return {
    ...item,
    active: isActive,
  };
}

function mapStaffNavGroups(
  sections: NavGroupDefinition[],
  pathname: string,
  options?: StudioNavOptions,
): LinkGroup[] {
  return sections.map((section) => ({
    id: section.id,
    label: section.label,
    icon: section.icon,
    collapsible: section.collapsible,
    items: filterNavItems(section.items, options).map((item) =>
      mapStaffNavLinkActive(item, pathname),
    ),
  }));
}

const staffNavSectionsBase: NavGroupDefinition[] = [
  {
    items: [
      { href: PageTypeAdmin.Classes, label: "Classes", icon: HiOutlineBookOpen },
      { href: PageTypeAdmin.Students, label: "Students", icon: HiOutlineUsers },
      { href: PageTypeAdmin.Parents, label: "Parents", icon: HiOutlineUserGroup },
      {
        href: PageTypeAdmin.Appointments,
        label: "Private Lessons",
        icon: HiOutlineCalendarDays,
      },
    ],
  },
  {
    id: "operations",
    label: "Operations",
    icon: HiOutlineClipboardDocumentList,
    collapsible: true,
    items: [
      {
        href: PageTypeAdmin.MakeupRequests,
        label: "Makeup requests",
        icon: HiOutlineArrowPathRoundedSquare,
      },
      { href: PageTypeAdmin.TimeClock, label: "Time Clock", icon: HiOutlineClock },
      {
        href: PageTypeAdmin.Tasks,
        label: "My Tasks",
        icon: HiOutlineCheckCircle,
      },
      { href: PageTypeAdmin.Tickets, label: "Tickets", icon: HiOutlineTicket },
      {
        href: PageTypeAdmin.Notifications,
        label: "Notifications",
        icon: HiOutlineBell,
      },
    ],
  },
  {
    id: "foundation",
    label: "Foundation",
    icon: HiOutlineRectangleStack,
    collapsible: true,
    items: [
      {
        href: PageTypeAdmin.Employees,
        label: "Employees",
        icon: HiOutlineAcademicCap,
      },
      {
        href: PageTypeAdmin.Contractors,
        label: "Contractors",
        icon: HiOutlineBriefcase,
      },
      {
        href: PageTypeAdmin.Dances,
        label: "Dances",
        icon: HiOutlineMusicalNote,
      },
      {
        href: PageTypeAdmin.Policies,
        label: "Waivers & Policies",
        icon: HiOutlineDocumentText,
      },
    ],
  },
  {
    items: [
      {
        href: PageTypeAdmin.Resources,
        label: "Resources",
        icon: HiOutlineQuestionMarkCircle,
      },
    ],
  },
  {
    items: [
      {
        href: PageTypeAdmin.Password,
        label: "Password",
        icon: HiOutlineLockClosed,
      },
    ],
  },
];

// Teacher links: Dashboard, Availability, Time Clock, Class Attendance
const teacherDashboardLink: Omit<LinkItem, "active"> = {
  href: PageTypeAdmin.Dashboard,
  label: "Dashboard",
  icon: HiOutlineHome,
};

const teacherLinkGroupsBase: Array<{ label?: string; items: Omit<LinkItem, "active">[] }> = [
  {
    items: [
      { href: PageTypeAdmin.Availability, label: "Availability", icon: HiOutlineCalendar },
      { href: PageTypeAdmin.TimeClock, label: "Time Clock", icon: HiOutlineClock },
      { href: PageTypeAdmin.Attendance, label: "Class Attendance", icon: HiOutlineClipboardDocumentList },
      { href: PageTypeAdmin.Notifications, label: "Notifications", icon: HiOutlineBell },
      { href: PageTypeAdmin.Tasks, label: "My Tasks", icon: HiOutlineCheckCircle },
    ],
  },
  {
    items: [
      {
        href: PageTypeAdmin.Resources,
        label: "Resources",
        icon: HiOutlineQuestionMarkCircle,
      },
    ],
  },
  {
    items: [
      {
        href: PageTypeAdmin.Password,
        label: "Password",
        icon: HiOutlineLockClosed,
      },
    ],
  },
];

const teacherLinksWithSubRoutes = [PageTypeAdmin.TimeClock, PageTypeAdmin.Attendance];

export const getTeacherLinks = (
  pathname: string,
  options?: StudioNavOptions,
): LinkItem[] => {
  const allItems = filterNavItems(
    [teacherDashboardLink, ...teacherLinkGroupsBase.flatMap((g) => g.items)],
    options,
  );
  return allItems.map((item) => {
    const pathSegments = pathname.split("/").filter(Boolean);
    const hrefSegments = item.href.split("/").filter(Boolean);
    const matches = hrefSegments.every((seg, i) => pathSegments[i] === seg);
    const isExact = pathSegments.length === hrefSegments.length;
    const hasSubRoutes = teacherLinksWithSubRoutes.includes(item.href as PageTypeAdmin);
    const isActive = hasSubRoutes ? pathname.startsWith(item.href) : matches && isExact;
    return { ...item, active: isActive };
  });
};

export const getTeacherLinkGroups = (
  pathname: string,
  options?: StudioNavOptions,
): LinkGroup[] => {
  const dashboardPathSegments = pathname.split("/").filter(Boolean);
  const dashboardHrefSegments = teacherDashboardLink.href.split("/").filter(Boolean);
  const dashboardActive =
    dashboardHrefSegments.every((seg, i) => dashboardPathSegments[i] === seg) &&
    dashboardPathSegments.length === dashboardHrefSegments.length;
  const dashboardLink: LinkItem = { ...teacherDashboardLink, active: dashboardActive };

  const groups = teacherLinkGroupsBase.map((group) => ({
    label: group.label,
    items: filterNavItems(group.items, options).map((item) => {
      const pathSegments = pathname.split("/").filter(Boolean);
      const hrefSegments = item.href.split("/").filter(Boolean);
      const matches = hrefSegments.every((seg, i) => pathSegments[i] === seg);
      const isExact = pathSegments.length === hrefSegments.length;
      const hasSubRoutes = teacherLinksWithSubRoutes.includes(item.href as PageTypeAdmin);
      const isActive = hasSubRoutes ? pathname.startsWith(item.href) : matches && isExact;
      return { ...item, active: isActive };
    }),
  }));

  return [{ items: [dashboardLink] }, ...groups];
};

const choreographerLinksBase: Omit<LinkItem, "active">[] = [
  {
    href: PageTypeAdmin.Dances,
    label: "Dances",
    icon: HiOutlineMusicalNote,
  },
  {
    href: PageTypeAdmin.Password,
    label: "Password",
    icon: HiOutlineLockClosed,
  },
];

export const getChoreographerLinkGroups = (
  pathname: string,
  options?: StudioNavOptions,
): LinkGroup[] => {
  const items = filterNavItems(choreographerLinksBase, options).map((item) => ({
    ...item,
    active: pathname === item.href || pathname.startsWith(`${item.href}/`),
  }));

  return [{ items }];
};

export const getStaffLinks = (
  pathname: string,
  options?: StudioNavOptions,
): LinkItem[] => {
  const allItems = filterNavItems(
    [staffDashboardLink, ...staffNavSectionsBase.flatMap((group) => group.items)],
    options,
  );
  return allItems.map((item) => mapStaffNavLinkActive(item, pathname));
};

export const getStaffLinkGroups = (
  pathname: string,
  options?: StudioNavOptions,
): LinkGroup[] => {
  const dashboardPathSegments = pathname.split("/").filter(Boolean);
  const dashboardHrefSegments = staffDashboardLink.href.split("/").filter(Boolean);
  const dashboardActive =
    dashboardHrefSegments.every((seg, i) => dashboardPathSegments[i] === seg) &&
    dashboardPathSegments.length === dashboardHrefSegments.length;
  const dashboardLink: LinkItem = { ...staffDashboardLink, active: dashboardActive };

  return [{ items: [dashboardLink] }, ...mapStaffNavGroups(staffNavSectionsBase, pathname, options)];
};

/** True for public marketing routes (not portal dashboard or auth). */
export function isPublicMarketingRoute(pathname: string): boolean {
  return (
    pathname.startsWith(PageTypeGlobal.Classes) ||
    pathname.startsWith(PageTypeGlobal.Teachers) ||
    pathname.startsWith(PageTypeGlobal.Locations) ||
    pathname.startsWith(PageTypeGlobal.Tickets) ||
    pathname === PageTypeGlobal.Contact ||
    pathname === PageTypeGlobal.Terms
  );
}

const PORTAL_LINKS_WITH_SUB_ROUTES: PageTypePortal[] = [
  PageTypePortal.Students,
];

function mapLinkItemsWithActiveState(
  items: Omit<LinkItem, "active">[],
  pathname: string,
): LinkItem[] {
  return items.map((item) => {
    const pathSegments = pathname.split("/").filter(Boolean);
    const hrefSegments = item.href.split("/").filter(Boolean);

    const matches = hrefSegments.every((seg, i) => pathSegments[i] === seg);
    const isExact = pathSegments.length === hrefSegments.length;

    const isClassesLink = item.href === PageTypeGlobal.Classes;
    const isTeachersLink = item.href === PageTypeGlobal.Teachers;
    const isLocationsNav =
      item.href === PageTypeGlobal.Locations ||
      item.href.startsWith(`${PageTypeGlobal.Locations}/`);
    const isTicketsLink = item.href === PageTypeGlobal.Tickets;
    const hasPortalSubRoutes = PORTAL_LINKS_WITH_SUB_ROUTES.includes(
      item.href as PageTypePortal,
    );

    let isActive = matches && isExact;
    if (isClassesLink) {
      isActive = pathname.startsWith(PageTypeGlobal.Classes);
    } else if (isTeachersLink) {
      isActive = pathname.startsWith(PageTypeGlobal.Teachers);
    } else if (isLocationsNav) {
      isActive = pathname.startsWith(PageTypeGlobal.Locations);
    } else if (isTicketsLink) {
      isActive = pathname.startsWith(PageTypeGlobal.Tickets);
    } else if (hasPortalSubRoutes) {
      isActive =
        pathname === item.href || pathname.startsWith(`${item.href}/`);
    }

    return {
      ...item,
      active: isActive,
    };
  });
}

/** Navbar + mobile drawer on public marketing pages. */
export const getPublicNavbarLinks = (
  isMember: boolean,
  pathname: string,
  options?: {
    locationsNav?: { href: string; label: string } | null;
    ticketsNav?: { href: string; label: string } | null;
  },
): LinkItem[] => {
  const items = isMember ? linkItemsPublicMemberBase : linkItemsGuestBase;
  const filtered = items
    .filter((item) => {
      if (item.href === PageTypeGlobal.Locations) {
        return options?.locationsNav != null;
      }
      if (item.href === PageTypeGlobal.Tickets) {
        return options?.ticketsNav != null;
      }
      return true;
    })
    .map((item) => {
      if (item.href === PageTypeGlobal.Locations && options?.locationsNav) {
        return {
          ...item,
          href: options.locationsNav.href,
          label: options.locationsNav.label,
        };
      }
      return item;
    });
  return mapLinkItemsWithActiveState(filtered, pathname);
};

export const getPortalLinks = (
  isMember: boolean,
  pathname: string,
  options?: StudioNavOptions,
): LinkItem[] => {
  const items = isMember ? linkItemsMemberBase : linkItemsGuestBase;
  return mapLinkItemsWithActiveState(filterNavItems(items, options), pathname);
};

export const getAdminLinks = (
  pathname: string,
  options?: StudioNavOptions,
): LinkItem[] => {
  const allItems = filterNavItems(
    [adminDashboardLink, ...adminNavSectionsBase.flatMap((group) => group.items)],
    options,
  );

  return allItems.map((item) => mapAdminNavLinkActive(item, pathname));
};

export const getAdminLinkGroups = (
  pathname: string,
  options?: StudioNavOptions,
): LinkGroup[] => {
  const dashboardPathSegments = pathname.split("/").filter(Boolean);
  const dashboardHrefSegments = adminDashboardLink.href.split("/").filter(Boolean);
  const dashboardActive =
    dashboardHrefSegments.every((seg, i) => dashboardPathSegments[i] === seg) &&
    dashboardPathSegments.length === dashboardHrefSegments.length;

  const dashboardLink: LinkItem = {
    ...adminDashboardLink,
    active: dashboardActive,
  };

  return [
    { items: [dashboardLink] },
    ...mapAdminNavGroups(adminNavSectionsBase, pathname, options),
  ];
};
